import { Result, Tabs } from 'antd';
import { useContext } from 'react';
import {
  DATE_FORMATS,
  PERMISSION_TYPE,
  PERMISSIONS_KEY,
  TABS,
  USER_ROLES,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';

import AccessControl from '../../../../../AccessControl';
import { AppContext } from '../../../../../AppContext';
import NewOrderTable from '../../../../orders/modules/combined-orders/components/NewOrderTable';
import UserReturnTable from '../../../../orders/modules/combined-orders/components/ReturnTable';
import EditEndUser from '../pages/EditEndUser';
import SubscriptionDetails from '../pages/SubscriptionDetails';
import SubscriptionHistoryDetails from '../pages/SubscriptionHistoryDetails';
import TransactionDetails from '../pages/TransactionDetails';
import ViewEndUser from '../pages/ViewEndUser';
import OrderDetails from '../pages/ViewOrderDetails';
import ViewSubscriptionDetails from '../pages/ViewSubscriptionDetails';

const EndUsersTabsForDrawer = ({ activeTabKey, setActiveTabKey }) => {
  const {
    state: { endUserDetails, permissions, currentUser, endUsersOnboardingDate },
  } = useContext(AppContext);

  const {
    id: endUserId,
    userNumber: endUserNumber,
    onboardedOn,
  } = endUserDetails;
  const checkPermission = (permissionKey, permissionType) =>
    hasPermission(permissionKey, permissionType, permissions) ||
    currentUser?.roles?.includes(USER_ROLES?.LIBRARIAN?.key);
  const errorSubTitle = 'Sorry, you are not authorized to access this section.';

  const NoAccessComponent = () => (
    <Result status="403" title="403" subTitle={errorSubTitle} />
  );

  const renderTabContent = (tabKey) => {
    switch (tabKey) {
      case TABS?.BASIC_DETAILS?.key: {
        const hasEditPermission = checkPermission(
          PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS,
          PERMISSION_TYPE?.UPDATE,
        );
        const hasViewPermission = checkPermission(
          PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS,
          PERMISSION_TYPE?.READ,
        );

        if (hasEditPermission) {
          return (
            <EditEndUser
              isDrawerView
              endUserId={endUserId}
              endUserNumber={endUserNumber}
            />
          );
        }
        if (hasViewPermission) {
          return (
            <ViewEndUser
              isDrawerView
              endUserId={endUserId}
              endUserNumber={endUserNumber}
            />
          );
        }
        return <NoAccessComponent />;
      }
      case TABS?.SUBSCRIPTION_PLAN_DETAILS?.key: {
        const hasEditPermission = checkPermission(
          PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS,
          PERMISSION_TYPE?.UPDATE,
        );
        const hasViewPermission = checkPermission(
          PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS,
          PERMISSION_TYPE?.READ,
        );

        if (hasEditPermission) {
          return (
            <SubscriptionDetails
              isDrawerView
              endUserId={endUserId}
              endUserNumber={endUserNumber}
            />
          );
        }
        if (hasViewPermission) {
          return (
            <ViewSubscriptionDetails
              isDrawerView
              endUserId={endUserId}
              endUserNumber={endUserNumber}
            />
          );
        }
        return <NoAccessComponent />;
      }

      case TABS?.ORDER_DETAILS?.key:
        return (
          <AccessControl
            noButton
            errorSubTitle={errorSubTitle}
            permissionKey={PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS}
          >
            <OrderDetails
              endUserId={endUserId}
              endUserNumber={endUserNumber}
              isDrawerView
            />
          </AccessControl>
        );

      case TABS?.TRANSACTION_DETAILS?.key:
        return (
          <AccessControl
            errorSubTitle={errorSubTitle}
            permissionKey={PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS}
            noButton
          >
            <TransactionDetails
              endUserId={endUserId}
              endUserNumber={endUserNumber}
              isDrawerView
            />{' '}
          </AccessControl>
        );

      case TABS?.SUBSCRIPTION_HISTORY?.key:
        return (
          <AccessControl
            errorSubTitle={errorSubTitle}
            noButton
            permissionKey={PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS}
          >
            <SubscriptionHistoryDetails
              endUserId={endUserId}
              endUserNumber={endUserNumber}
              isDrawerView
            />{' '}
          </AccessControl>
        );

      case TABS?.ORDERS?.key:
        return (
          <AccessControl
            errorSubTitle={errorSubTitle}
            noButton
            permissionKey={PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS}
          >
            <NewOrderTable userId={endUserId} isSystemUsersTab />{' '}
          </AccessControl>
        );

      case TABS?.RETURN?.key:
        return (
          <AccessControl
            errorSubTitle={errorSubTitle}
            noButton
            permissionKey={PERMISSIONS_KEY?.SYSTEM_USERS_END_USERS}
          >
            <UserReturnTable userId={endUserId} isSystemUsersTab />{' '}
          </AccessControl>
        );

      default:
        return null;
    }
  };

  const tabList = [
    {
      key: TABS?.BASIC_DETAILS?.key,
      label: TABS?.BASIC_DETAILS?.label,
      children: renderTabContent(TABS?.BASIC_DETAILS?.key),
    },
    {
      key: TABS?.SUBSCRIPTION_PLAN_DETAILS?.key,
      label: TABS?.SUBSCRIPTION_PLAN_DETAILS?.label,
      children: renderTabContent(TABS?.SUBSCRIPTION_PLAN_DETAILS?.key),
    },
    {
      key: TABS?.ORDER_DETAILS?.key,
      label: TABS?.ORDER_DETAILS?.label,
      children: renderTabContent(TABS?.ORDER_DETAILS?.key),
    },
    {
      key: TABS?.TRANSACTION_DETAILS?.key,
      label: TABS?.TRANSACTION_DETAILS?.label,
      children: renderTabContent(TABS?.TRANSACTION_DETAILS?.key),
    },
    {
      key: TABS?.SUBSCRIPTION_HISTORY?.key,
      label: TABS?.SUBSCRIPTION_HISTORY?.label,
      children: renderTabContent(TABS?.SUBSCRIPTION_HISTORY?.key),
    },
    !currentUser?.roles?.includes(USER_ROLES?.SALES?.key) && {
      key: TABS?.ORDERS?.key,
      label: TABS?.ORDERS?.label,
      children: renderTabContent(TABS?.ORDERS?.key),
    },
    !currentUser?.roles?.includes(USER_ROLES?.SALES?.key) && {
      key: TABS?.RETURN?.key,
      label: TABS?.RETURN?.label,
      children: renderTabContent(TABS?.RETURN?.key),
    },
  ];

  const onTabChange = (key) => {
    setActiveTabKey(key);
  };

  return (
    <div className="card-container">
      <Tabs
        activeKey={activeTabKey}
        type="card"
        className="full-width"
        onChange={onTabChange}
        destroyInactiveTabPane
        items={tabList}
        tabBarExtraContent={
          activeTabKey === TABS?.BASIC_DETAILS?.key ? (
            <strong>
              Onboarded On :{' '}
              {formatDate(
                onboardedOn || endUsersOnboardingDate,
                DATE_FORMATS?.ONBOARD,
              ) || '-'}
            </strong>
          ) : null
        }
      />
    </div>
  );
};

export default EndUsersTabsForDrawer;
